import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, Lightbulb } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { queryClient } from "@/lib/queryClient";

interface TooltipConfig {
  id: string;
  title: string;
  description: string;
}

const tooltipConfigs: Record<string, TooltipConfig> = {
  home: {
    id: "home",
    title: "Panel Principal",
    description: "Aquí verás tu nivel, estadísticas y acceso rápido a todas las funciones de la app.",
  },
  matches: {
    id: "matches",
    title: "Gestión de Partidos",
    description: "Registra partidos, programa encuentros y revisa tu historial. Cada partido afecta tu nivel.",
  },
  leagues: {
    id: "leagues",
    title: "Ligas y Competencias",
    description: "Únete o crea ligas para competir. Sube en el ranking y gana logros especiales.",
  },
  social: {
    id: "social",
    title: "Comunidad",
    description: "Encuentra jugadores, agrega amigos y consulta el ranking global de la comunidad.",
  },
  profile: {
    id: "profile",
    title: "Tu Perfil",
    description: "Personaliza tu perfil, revisa tu evolución de nivel y consulta tus logros.",
  },
};

interface ContextualTooltipProps {
  section: string;
  tooltipsSeen?: string[] | null;
  className?: string;
}

export function ContextualTooltip({ section, tooltipsSeen, className = "" }: ContextualTooltipProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  const config = tooltipConfigs[section];
  const alreadySeen = tooltipsSeen?.includes(section) ?? true;

  const handleDismiss = async () => {
    setIsDismissed(true);
    try {
      await apiRequest("POST", `/api/tooltips/seen/${section}`);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    } catch (e) {
    }
  };

  if (!config || isDismissed || alreadySeen) return null;

  return (
    <Card 
      className={`bg-primary text-primary-foreground shadow-lg animate-in fade-in slide-in-from-top-2 duration-300 ${className}`}
      data-testid={`tooltip-${section}`}
    >
      <div className="relative p-3">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-1 right-1 h-6 w-6 text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10"
          onClick={handleDismiss}
          data-testid={`button-dismiss-tooltip-${section}`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
        <div className="flex gap-3 pr-6">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center">
            <Lightbulb className="h-4 w-4" />
          </div>
          <div className="space-y-0.5">
            <h4 className="font-semibold text-sm">{config.title}</h4>
            <p className="text-xs text-primary-foreground/80 leading-relaxed">
              {config.description}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
